/*
 * PWA do modo kiosk — instalação SÓ no tablet/totem.
 *
 * Estratégia de escopo: o manifest e o service worker só são ativados quando o
 * aparelho está em modo tablet/totem (`ativarPwaKiosk`, chamado pelo
 * PwaKioskInstall). Assim o cliente comum (celular) nunca fica "instalável" e
 * não recebe convite. A captura do `beforeinstallprompt` é global e cedo (para
 * suprimir o convite nativo em qualquer caso), mas só acionamos o prompt via
 * botão no kiosk.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferido: BeforeInstallPromptEvent | null = null;
let instalado = false;
let pwaAtivado = false;
const ouvintes = new Set<() => void>();

function notificar(): void {
  ouvintes.forEach((cb) => cb());
}

/**
 * Registra cedo (main.tsx) a captura do `beforeinstallprompt`: dá
 * `preventDefault` para o navegador NÃO mostrar o próprio convite (nem para o
 * cliente comum) e guarda o evento para acionarmos via botão só no kiosk.
 */
export function inicializarCapturaInstall(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferido = e as BeforeInstallPromptEvent;
    notificar();
  });
  window.addEventListener('appinstalled', () => {
    deferido = null;
    instalado = true;
    notificar();
  });
}

/** De quanto em quanto tempo o aparelho procura versão nova. */
const INTERVALO_UPDATE_MS = 60 * 60 * 1000;

/**
 * Ativa o PWA (injeta o `<link rel="manifest">` + registra o service worker).
 * Só deve ser chamado no modo tablet/totem. Idempotente. O SW só é registrado
 * em produção (evita conflito com o HMR do Vite em dev).
 *
 * `podeRecarregar` decide QUANDO trocar de versão. Tablet de mesa fica ligado
 * semanas sem ninguém recarregar: sem isso o aparelho roda para sempre o código
 * do dia em que foi instalado. Mas recarregar no meio do pedido apagaria o
 * carrinho do cliente — então a troca espera o aparelho ficar ocioso.
 */
export function ativarPwaKiosk(opcoes: { podeRecarregar?: () => boolean } = {}): void {
  if (pwaAtivado) return;
  pwaAtivado = true;

  if (!document.querySelector('link[rel="manifest"]')) {
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = '/manifest.webmanifest';
    document.head.appendChild(link);
  }

  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;

  // Já havia um SW no comando? Se não havia, o `controllerchange` que vem logo
  // após esta primeira instalação NÃO é troca de versão — a página já está
  // rodando o código mais novo, e recarregar ali seria um refresh à toa.
  const jaTinhaControlador = !!navigator.serviceWorker.controller;
  let versaoNovaPendente = false;
  let recarregando = false;

  function trocarSePuder(): void {
    if (!versaoNovaPendente || recarregando) return;
    if (opcoes.podeRecarregar && !opcoes.podeRecarregar()) return;
    recarregando = true;
    window.location.reload();
  }

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!jaTinhaControlador) return;
    versaoNovaPendente = true;
    trocarSePuder();
  });

  navigator.serviceWorker
    .register('/sw.js')
    .then((registro) => {
      const procurarVersao = (): void => {
        void registro.update().catch(() => undefined);
      };

      window.setInterval(procurarVersao, INTERVALO_UPDATE_MS);
      // Também quando a tela volta a ficar visível: é quando a loja abre.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        procurarVersao();
        trocarSePuder();
      });
      // Ficou ocioso depois que a versão nova chegou: aproveita a janela.
      window.setInterval(trocarSePuder, 30_000);
    })
    .catch(() => {
      /* SW é best-effort: sem ele o app funciona, só não fica instalável */
    });
}

/** Já está rodando como app instalado (standalone/fullscreen)? */
export function ehAppInstalado(): boolean {
  return (
    instalado ||
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/** Há um convite de instalação pendente (e ainda não está instalado)? */
export function podeInstalar(): boolean {
  return deferido !== null && !ehAppInstalado();
}

/** Dispara o prompt nativo de instalação. Retorna o desfecho. */
export async function instalarPwa(): Promise<'accepted' | 'dismissed' | 'indisponivel'> {
  if (!deferido) return 'indisponivel';
  await deferido.prompt();
  const escolha = await deferido.userChoice.catch(() => ({ outcome: 'dismissed' as const }));
  deferido = null;
  notificar();
  return escolha.outcome;
}

/** Assina mudanças de disponibilidade do convite. Retorna a função de cancelar. */
export function assinarInstall(cb: () => void): () => void {
  ouvintes.add(cb);
  return () => {
    ouvintes.delete(cb);
  };
}
